import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, CustomConfirm, DATE_FORMATS_FNS, FileDragDrop, useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import "modules/AppointmentDetails/components/Interventions/components/NoteAttachments/NoteAttachments.scss";
import { useDeleteInterventionAttachmentMutation, useUploadInterventionAttachmentMutation } from "modules/AppointmentDetails/components/Interventions/hooks";
import { downloadFile, getNameFromURL } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { Dispatch, SetStateAction } from "react";

export interface NoteAttachment {
  id?: number;
  url: string;
  name: string;
  type: string;
  created_on?: string;
}

type NoteAttachmentsProps = {
  appointmentId: number;
  attachments?: NoteAttachment[];
  setAttachments: Dispatch<SetStateAction<NoteAttachment[]>>;
  readOnly?: boolean;
};

export const NoteAttachments = ({ appointmentId, attachments, setAttachments, readOnly = false }: NoteAttachmentsProps) => {
  const { t } = useTranslation();
  const [selectedDeleteAttachmentURL, setSelectedDeleteAttachmentURL] = useState("");
  const { mutateAsync: uploadAttachment } = useUploadInterventionAttachmentMutation();
  const { isPending: isDeleteInterventionAttachmentPending } = useDeleteInterventionAttachmentMutation();
  const canAddAttachments = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.InterventionAttachments);

  const handleUploadAttachment = async (file: File) => {
    const extension = file.name.lastIndexOf(".") > -1 ? file.name.slice(file.name.lastIndexOf(".") + 1) : "unknown";
    const uploadFile = new File([file], file.name, { type: file.type || extension });

    const attachmentUrl = await uploadAttachment({ appointment_id: appointmentId, file: uploadFile });
    if (!attachmentUrl) return;

    const attachment = {
      url: attachmentUrl,
      type: file.type,
      name: getNameFromURL(attachmentUrl)
    };

    setAttachments(currentAttachments => [...currentAttachments, attachment]);
  };

  const handleFileUpload = (files: File[]) => {
    files.forEach(file => handleUploadAttachment(file));
  };

  const onCancelDeleteAttachment = () => {
    setSelectedDeleteAttachmentURL("");
  };

  const onDeleteAttachment = (url: string) => {
    setSelectedDeleteAttachmentURL(url);
  };

  const handleConfirmDeleteAttachment = () => {
    if (selectedDeleteAttachmentURL) setAttachments(prev => prev.filter(attachment => attachment.url !== selectedDeleteAttachmentURL));
    setSelectedDeleteAttachmentURL("");
  };

  const handleOnAttachmentDownloadClick = async (url: string) => {
    await downloadFile(url);
  };

  const renderListView = () =>
    !!attachments?.length && (
      <div className="attachment-container">
        {attachments.map(attachment => {
          return (
            <div key={attachment.id || -1} className="attachment-view">
              <div className="attachment-name" onClick={() => handleOnAttachmentDownloadClick(attachment.url)}>
                <Icon className="image" />
                <p className="ellipsis">{attachment.name}</p>
              </div>
              <div className="attachment-delete">
                {attachment.created_on && <p>{`${t("v8_uploaded_on", "Uploaded on")} ${formatDate(attachment.created_on, DATE_FORMATS_FNS.dateMonthYearTime)}`}</p>}
                {!readOnly && <Icon className="trash grey" onClick={() => onDeleteAttachment(attachment.url)} />}
              </div>
            </div>
          );
        })}
      </div>
    );

  return (
    <div className="NoteAttachments">
      {!readOnly && <FileDragDrop disabled={!canAddAttachments} id="intervention-image" onFileUpload={handleFileUpload} required={false} />}
      {renderListView()}
      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        handleCancel={onCancelDeleteAttachment}
        isOpen={!!selectedDeleteAttachmentURL}
        isLoading={isDeleteInterventionAttachmentPending}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
        handleConfirm={handleConfirmDeleteAttachment}
      />
    </div>
  );
};
